import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { getCart, updateItemQty, removeItem, applyCoupon, removeCoupon, clearCart, cartText, cartPresentation } from "./api.js";
import CartSteps from "./components/CartSteps.jsx";
import CartItems from "./components/CartItems.jsx";
import CartSidebar from "./components/CartSidebar.jsx";
import CartEmpty from "./components/CartEmpty.jsx";
import { azText, azFeature } from '../core/config.js';

export default function CartApp({ checkoutUrl, shopUrl }) {
	const presentation = cartPresentation();
	const [cart, setCart] = useState(null);
	const [loading, setLoading] = useState(true);
	const [busyKeys, setBusyKeys] = useState(new Set());
	const [error, setError] = useState(null);

	useEffect(() => {
		if (cart) window.dispatchEvent(new CustomEvent("ai-zippy-cart-updated", { detail: cart }));
	}, [cart]);

	// Load cart
	useEffect(() => {
		getCart()
			.then(setCart)
			.catch(() => setError(azText('cart.load_failed', 'Failed to load cart')))
			.finally(() => setLoading(false));
	}, []);

	// Note: the Zippy CRM points-tender listener moved to CheckoutApp in
	// plugin v1.13.0. Cart no longer hosts the redemption widget, so there's
	// nothing for this surface to subscribe to.

	const markBusy = useCallback((key, busy) => {
		setBusyKeys((prev) => {
			const next = new Set(prev);
			busy ? next.add(key) : next.delete(key);
			return next;
		});
	}, []);

	const mutating = useRef(false);
	const mutate = useCallback(async (key, operation, fallback, coupon = false) => {
		if (mutating.current) return azText('cart.busy', 'Please wait for the cart to update.');
		mutating.current = true;
		markBusy(key, true);
		setError(null);
		try {
			setCart(await operation());
			return null;
		} catch (err) {
			const message = cartText(err.message || fallback);
			if (!coupon) setError(message);
			// A multi-item clear can partially succeed. Recover the authoritative snapshot.
			try { setCart(await getCart()); } catch { /* Keep the last known cart. */ }
			return message;
		} finally {
			mutating.current = false;
			markBusy(key, false);
		}
	}, [markBusy]);
	const handleUpdateQty = useCallback((key, qty) => mutate(key, () => updateItemQty(key, qty), azText('cart.update_failed', 'Failed to update quantity')), [mutate]);
	const handleRemove = useCallback((key) => mutate(key, () => removeItem(key), azText('cart.remove_failed', 'Failed to remove item')), [mutate]);
	const handleClearCart = useCallback(() => {
		if (cart?.items?.length) return mutate('clear', () => clearCart(cart.items), azText('cart.clear_failed', 'Failed to clear cart'));
	}, [cart, mutate]);
	const handleApplyCoupon = useCallback((code) => mutate('coupon', () => applyCoupon(code), azText('cart.coupon_failed', 'Failed to apply coupon'), true), [mutate]);
	const handleRemoveCoupon = useCallback((code) => mutate('coupon', () => removeCoupon(code), azText('cart.coupon_remove_failed', 'Failed to remove coupon')), [mutate]);

	// Dismiss error
	useEffect(() => {
		if (!error || !cart) return;
		const t = setTimeout(() => setError(null), 4000);
		return () => clearTimeout(t);
	}, [error, cart]);

	// Loading skeleton
	if (loading && !cart) {
		return (
			<div className="zc">
				{presentation.showSteps !== false && <CartSteps current={1} />}
				<div className="zc__skeleton">
					<div className="zc__skeleton-items">
						{[1, 2, 3].map((i) => (
							<div key={i} className="zc__skeleton-row" />
						))}
					</div>
					<div className="zc__skeleton-sidebar" />
				</div>
			</div>
		);
	}

	if (!cart && error) {
		return <div className="zc"><div className="zc__error" role="alert">{error}</div></div>;
	}

	// Empty cart. presentation.empty.inLayout keeps the items column and summary
	// (zero totals, checkout disabled) and shows the message inside the column.
	const isEmpty = !cart?.items?.length;
	if (isEmpty && !presentation.empty?.inLayout) {
		return (
			<div className="zc">
				{presentation.showSteps !== false && <CartSteps current={1} />}
				{presentation.title && <h1 className="zc-items__title">{presentation.title}</h1>}
				<CartEmpty shopUrl={shopUrl} content={presentation.empty} />
			</div>
		);
	}

	const items = cart.items || [];
	const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
	const policy = azFeature('cart_policy') ? cart.extensions?.['ai-zippy-cart-policy'] : null;
	const messages = (policy?.messages || []).map((message) => ({ ...message, title: cartText(message.title || ''), text: cartText(message.text) }));
	const cartErrors = (cart.errors || []).map((entry) => ({ ...entry, message: cartText(entry.message) }));
	const canCheckout = !isEmpty && policy?.can_checkout !== false && !loading && !busyKeys.size && !cart.errors?.length;

	// Same markup as CartAssets::renderPolicy() and the mini-cart drawer.
	const notices = <>
			{error && <div className="zc__error" role="alert">{error}</div>}
			{(messages.length > 0 || cartErrors.length > 0) && <div className="zc__policy az-policy" aria-live="polite">
				{messages.map((message, index) => (
					<div className={`zc__policy-message az-policy__message az-policy__message--${message.type}`} key={index}>
						{message.title && <p className="az-policy__title">{message.title}</p>}
						<p className="az-policy__text">
							{message.text}
							{message.actions?.map((action, i) => (
								<Fragment key={i}>
									{i > 0 && message.separator && <>{' '}<span className="az-policy__sep">{cartText(message.separator)}</span></>}
									{' '}<a className="az-policy__action" href={action.url}>{cartText(action.label)}</a>
								</Fragment>
							))}
						</p>
					</div>
				))}
				{cartErrors.filter((entry) => !messages.some((message) => message.text === entry.message || message.title === entry.message)).map((entry, index) => (
					<div className="zc__error" key={index}>{entry.message}</div>
				))}
			</div>}
		</>;

	return (
		<div className="zc">
			{!presentation.noticesInItems && notices}
			{presentation.showSteps !== false && <CartSteps current={1} />}

			<div className="zc__layout">
				<CartItems
					presentation={presentation}
					notices={presentation.noticesInItems ? notices : null}
					empty={isEmpty ? <CartEmpty shopUrl={shopUrl} content={presentation.empty} /> : null}
					items={items}
					itemCount={itemCount}
					busyKeys={busyKeys}
					loading={loading || busyKeys.size > 0}
					onUpdateQty={handleUpdateQty}
					onRemove={handleRemove}
					onClearCart={handleClearCart}
				/>
				<CartSidebar
					presentation={presentation}
					shopUrl={shopUrl}
					totals={cart.totals}
					coupons={cart.coupons}
					checkoutUrl={checkoutUrl}
					canCheckout={canCheckout}
					busy={loading || busyKeys.size > 0}
					onApplyCoupon={handleApplyCoupon}
					onRemoveCoupon={handleRemoveCoupon}
				/>
			</div>
		</div>
	);
}
